import {
	Card,
	CardContent,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from '@mui/material';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import {
	getConfirmedOrders,
	getConfirmedOrdersLoader,
	getOrderDetailsCombined,
} from 'redux/reducers/orders/selectors';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';

export const AmountBreakdown: FC = () => {
	const orders = useSelector(getConfirmedOrders);
	const ordersIsLoading = useSelector(getConfirmedOrdersLoader);
	const orderDetails = useSelector(getOrderDetailsCombined);
	const returnedOrders = orderDetails?.orderReturns;
	const discountOrders = orderDetails?.orderDiscounts;
	return (
		<div>
			{orders && !ordersIsLoading && orders?.data.length > 0 && (
				<Card
					sx={{
						mb: 2,
						'&': {
							borderRadius: '12px',
						},
					}}
				>
					<CardContent>
						<Typography sx={{ fontSize: 14 }} color="primary" gutterBottom>
							Amount Breakdown
						</Typography>
						<TableContainer>
							<Table>
								<TableBody>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>
											Merchandise cost:
										</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={orders?.data?.[0]?.totalMerchCost ?? 0}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>Delivery Cost:</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={orders?.data?.[0]?.deliveryCost ?? 0}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>Taxes:</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={orders?.data?.[0]?.totalVat ?? 0}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>Customs Duties:</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={orders?.data?.[0]?.totalCustomDuties ?? 0}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>
											Customs Procedure Fee:
										</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={orders?.data?.[0]?.totalDpoFee ?? 0}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }}>EAS Fee:</TableCell>
										<TableCell align="right" sx={{ padding: '5px', borderBottom: 0 }}>
											<AmountAndCurrency
												amount={orders?.data?.[0]?.easFeesDue ?? 0}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={0.9}
											/>
										</TableCell>
									</TableRow>

									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
									</TableRow>
									<TableRow>
										<TableCell
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												// borderTop: '1px solid #0000001f',
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											Total Amount:
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												// borderBottom: '1px solid #0000001f',
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											<AmountAndCurrency
												amount={
													(orders?.data?.[0]?.totalMerchCost ?? 0) +
													(orders?.data?.[0]?.deliveryCost ?? 0) +
													(orders?.data?.[0]?.totalVat ?? 0) +
													(orders?.data?.[0]?.totalCustomDuties ?? 0) +
													(orders?.data?.[0]?.easFeesDue ?? 0) +
													(orders?.data?.[0]?.totalDpoFee ?? 0)
												}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={1}
												bold
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px' }} />
										<TableCell sx={{ padding: '5px' }} />
									</TableRow>
									<TableRow>
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
										<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
									</TableRow>

									<TableRow>
										<TableCell
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												// borderTop: '1px solid #0000001f',
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											Total Returns:
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												// borderTop: '1px solid #0000001f',
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											<AmountAndCurrency
												amount={
													returnedOrders?.reduce(
														(accumulator, item) => accumulator + (item?.totalReturnAmount ?? 0),
														0,
													) ?? 0
												}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={1}
												bold
											/>
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												// borderTop: '1px solid #0000001f',
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											Total Post Sale Discounts:
										</TableCell>
										<TableCell
											align="right"
											sx={{
												padding: '5px',
												fontWeight: 'bold',
												borderBottom: 0,
												// borderTop: '1px solid #0000001f',
												fontSize: '1rem',
												color: 'primary.main',
											}}
										>
											<AmountAndCurrency
												amount={
													discountOrders?.reduce(
														(accumulator, item) => accumulator + (item?.totalDiscountAmount ?? 0),
														0,
													) ?? 0
												}
												currency={orders?.data?.[0]?.paymentCurrency}
												size={1}
												bold
											/>
										</TableCell>
									</TableRow>
									{+(orders?.data?.[0]?.importThreshold ?? 0) > 0 && (
										<>
											<TableRow>
												<TableCell sx={{ padding: '5px' }} />
												<TableCell sx={{ padding: '5px' }} />
											</TableRow>
											<TableRow>
												<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
												<TableCell sx={{ padding: '5px', borderBottom: 0 }} />
											</TableRow>

											<TableRow>
												<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
													Calculated Import Threshold:
													{/* ({orderCurrency}→ {thresholdCurrency}): */}
												</TableCell>
												<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
													<AmountAndCurrency
														amount={+orders.data[0].importThreshold}
														currency={orders.data[0].thresholdCurrency}
														size={0.9}
													/>
												</TableCell>
											</TableRow>
										</>
									)}
									{+(orders?.data?.[0]?.importThreshold ?? 0) > 0 &&
										orders.data[0].paymentCurrency !== orders.data[0].thresholdCurrency && (
											<TableRow>
												<TableCell sx={{ padding: '2px', borderBottom: 0 }}>
													Threshold Exchange Rate Applied:
													{/* ({orderCurrency}→ {thresholdCurrency}): */}
												</TableCell>

												<TableCell align="right" sx={{ padding: '2px', borderBottom: 0 }}>
													<AmountAndCurrency
														amount={1}
														currency={orders.data[0]?.paymentCurrency}
														size={0.9}
														afterPoint={0}
													/>
													{/* 1 <OrderCurrency currency={orderCurrency} size={0.6} /> */} ={' '}
													<AmountAndCurrency
														amount={orders.data[0].intrinsicThresholdExchangeRate}
														currency={orders.data[0].thresholdCurrency}
														size={0.9}
														afterPoint={6}
													/>
													{/* {intrinsicThresholdExchangeRate}{' '}
												<OrderCurrency currency={thresholdCurrency} size={0.6} /> */}
												</TableCell>
											</TableRow>
										)}
								</TableBody>
							</Table>
						</TableContainer>
					</CardContent>
				</Card>
			)}
			{ordersIsLoading && (
				<Skeleton variant="rectangular" height="300px" sx={{ borderRadius: '12px' }} />
			)}
		</div>
	);
};
